import { Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { useOpeningDetails } from '@/contexts/opening-context';
import { useShowHiringGoalEmptyState } from '@/components/pages/channels/use-show-hiring-goal-empty-state';

import { Analytics } from './analytics';
import { AutomationStatusBanner } from './automation-status-banner';
import { HiringGoalEmptyState } from './hiring-goal-empty-state';
import { ChannelsSection } from './channels-section';
import { GeneralRecommendation } from './general-recommendations';
import { PostingUnavailableBanner } from './posting-unavailable-banner';
import { SourcingOpenings } from './sourcing-openings';

/**
 * Owns its own Suspense boundary (like every section below) so deciding
 * whether to show the empty state doesn't gate the other sections' mounts.
 */
function HiringGoalEmptyStateSection() {
  const { hiringGoal } = useOpeningDetails();
  const showHiringGoalEmptyState = useShowHiringGoalEmptyState(hiringGoal);

  if (!showHiringGoalEmptyState) return null;
  return <HiringGoalEmptyState />;
}

/**
 * Each section below owns its own Suspense boundary and independently
 * checks `useShowHiringGoalEmptyState()` to decide whether to render.
 * They're rendered as plain siblings (not gated behind a shared
 * `useOpeningDetails()` call here) so their fetches — several of which
 * intentionally kick off before their own `useOpeningDetails()` call, to
 * run in parallel with the funnel-details API — all start immediately
 * instead of waiting on whichever section happens to be listed first.
 */
function ChannelsContent() {
  return (
    <>
      <PostingUnavailableBanner />
      <Suspense fallback={null}>
        <HiringGoalEmptyStateSection />
      </Suspense>
      <AutomationStatusBanner />
      <Analytics />
      <GeneralRecommendation />
      <Suspense fallback={null}>
        <SourcingOpenings />
      </Suspense>
      <ChannelsSection />
      <Outlet />
    </>
  );
}

export function Channels() {
  return (
    <Suspense fallback={<Channels.Skeleton />}>
      <ChannelsContent />
    </Suspense>
  );
}

Channels.Skeleton = function ChannelsSkeleton() {
  return (
    <>
      <Analytics.Skeleton />
      <ChannelsSection.Skeleton />
    </>
  );
};
